'use client';

import { useState, useMemo, useEffect, useRef } from 'react';

type Event = {
  year: number;
  title: string;
  cat: string;
  desc: string;
  pins: string[];
};

const EVENTS: Event[] = [
  { year: 2023, title: 'Запуск INTERIM', cat: 'Запуск', desc: 'Первые проекты в ритейле и логистике, 50+ закрытий.', pins: ['RPO v1', 'SLA 48ч'] },
  { year: 2023, title: 'Первые партнёрства', cat: 'Партнёрство', desc: 'Интеграции с ATS и CRM, совместные вебинары.', pins: ['ATS', 'CRM'] },
  { year: 2024, title: 'MSP-портфель', cat: 'Партнёрство', desc: 'Подключили пул субподрядчиков, единые метрики.', pins: ['MSP', 'оценка качества'] },
  { year: 2024, title: 'Блок отчётности BI', cat: 'Продукт', desc: 'Дашборды воронки, прогноз потока кандидатов.', pins: ['BI', 'прогноз'] },
  { year: 2024, title: '100-й клиент', cat: 'Клиенты', desc: 'Расширение в e-commerce и клиентском сервисе.', pins: ['SLA 24ч', '90% явка'] },
  { year: 2025, title: 'География 12 регионов', cat: 'География', desc: 'Федеральные контракты, удалённые форматы.', pins: ['remote', 'региональная сеть'] },
  { year: 2025, title: 'Команда 40+ человек', cat: 'Команда', desc: 'Сильный сорсинг, стандарты интервью, наставничество.', pins: ['обучение', '30/60/90'] },
  { year: 2025, title: 'Премия «Customer Success»', cat: 'Награда', desc: 'Отмечены за прозрачность SLA и клиентоцентричность.', pins: ['CSAT', 'SLA'] },
  { year: 2026, title: 'План: AI-ассистент рекрутёра', cat: 'Продукт', desc: 'Полуавтоматизация сорсинга и обратной связи.', pins: ['AI', 'автоматизация'] },
];

const categories = Array.from(new Set(EVENTS.map(e => e.cat)));
const currentYear = 2025; 

const TimelineEvent = ({ event }: { event: Event }) => (
  <li className="relative dot">
    <div className="card rounded-2xl p-5">
      <div className="flex items-center gap-2 text-sm text-gray-300">
        <span className="cat">{event.cat}</span>
        <span>{event.year}</span>
      </div>
      <h3 className="text-xl font-semibold mt-1">{event.title}</h3>
      <p className="text-sm text-gray-200 mt-1">{event.desc}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {event.pins.map((pin, index) => (
          <span key={index} className="cat">{pin}</span>
        ))}
      </div>
    </div>
  </li>
);

const animateCounter = (el: Element) => {
  const to = Number(el.getAttribute('data-to'));
  const duration = 900;
  const start = performance.now();
  const step = (now: number) => {
    const progress = Math.min(1, (now - start) / duration);
    (el as HTMLElement).textContent = Math.floor(to * progress).toString();
    if (progress < 1) {
      requestAnimationFrame(step);
    }
  };
  requestAnimationFrame(step);
};

export default function Home() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [yearFrom, setYearFrom] = useState(2023);
  const [yearTo, setYearTo] = useState(2026);
  const countersRef = useRef<NodeListOf<Element> | null>(null);

  const filteredEvents = useMemo(() => {
    return EVENTS.filter(event => {
      const matchesYear = event.year >= yearFrom && event.year <= yearTo;
      const matchesCategory = !category || event.cat === category;
      const hay = `${event.title} ${event.desc} ${event.pins.join(' ')}`.toLowerCase();
      const matchesQuery = !query || hay.includes(query.toLowerCase());
      return matchesYear && matchesCategory && matchesQuery;
    }).sort((a, b) => b.year - a.year);
  }, [query, category, yearFrom, yearTo]);

  useEffect(() => {
    countersRef.current = document.querySelectorAll('.counter');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateCounter(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });

    countersRef.current.forEach(counter => observer.observe(counter));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100">
      <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
        style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}>
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap gap-3 text-sm">
            <a href="#timeline" className="chip px-3 py-1 rounded-full hover:bg-white/25">Вехи</a>
            <a href="#milestones" className="chip px-3 py-1 rounded-full hover:bg-white/25">Достижения</a>
            <a href="#media" className="chip px-3 py-1 rounded-full hover:bg-white/25">Медиа</a>
            <a href="#awards" className="chip px-3 py-1 rounded-full hover:bg-white/25">Награды</a>
            <a href="#docs" className="chip px-3 py-1 rounded-full hover:bg-white/25">Документы</a>
          </nav>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">История INTERIM</h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">Короткий путь от первого запуска к масштабированию: ключевые события, цифры и победы.</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#timeline" className="btn btn-ghost">Смотреть ленту</a>
            <a href="#docs" className="btn btn-accent">Материалы для прессы</a>
          </div>
        </div>
      </header>

      <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
        <section id="timeline">
          <div className="flex flex-wrap items-end gap-4 justify-between">
            <h2 className="text-3xl md:text-4xl font-bold">Вехи и события</h2>
            <span id="tl-count" className="text-sm text-gray-300">Событий: {filteredEvents.length}</span>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-5">
            <input
              id="tl-q"
              className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
              placeholder="Поиск по событиям…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <select
              id="tl-cat"
              className="rounded-xl px-4 py-3 text-gray-900"
              value={category}
              onChange={e => setCategory(e.target.value)}
            >
              <option value="">Категория: все</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <div className="rounded-xl px-4 py-3 card flex items-center gap-2">
              <label className="text-sm text-gray-200">С</label>
              <input
                id="y-from"
                type="range"
                min="2023"
                max="2026"
                step="1"
                value={yearFrom}
                onChange={e => setYearFrom(Number(e.target.value))}
                className="flex-1"
              />
              <span id="y-from-v" className="text-sm">{yearFrom}</span>
            </div>
            <div className="rounded-xl px-4 py-3 card flex items-center gap-2">
              <label className="text-sm text-gray-200">По</label>
              <input
                id="y-to"
                type="range"
                min="2023"
                max="2026"
                step="1"
                value={yearTo}
                onChange={e => setYearTo(Number(e.target.value))}
                className="flex-1"
              />
              <span id="y-to-v" className="text-sm">{yearTo}</span>
            </div>
          </div>

          <ol id="tl-list" className="relative border-l border-white/10 pl-8 mt-6 space-y-6">
            {filteredEvents.length > 0 ? (
              filteredEvents.map((event, index) => (
                <TimelineEvent key={index} event={event} />
              ))
            ) : (
              <p id="tl-empty" className="text-gray-300">Нет событий для выбранных фильтров.</p>
            )}
          </ol>
        </section>

        <section id="milestones">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Достижения в цифрах</h2>
          <div className="grid gap-6 md:grid-cols-4">
            <div className="card rounded-2xl p-6 text-center">
              <div className="text-5xl font-extrabold counter" data-to="96">0</div>
              <div className="text-xs text-gray-300 mt-1">средняя явка персонала</div>
            </div>
            <div className="card rounded-2xl p-6 text-center">
              <div className="text-5xl font-extrabold counter" data-to="19">0</div>
              <div className="text-xs text-gray-300 mt-1">снижение CPH, %</div>
            </div>
            <div className="card rounded-2xl p-6 text-center">
              <div className="text-5xl font-extrabold counter" data-to="24">0</div>
              <div className="text-xs text-gray-300 mt-1">SLA на замену, ч</div>
            </div>
            <div className="card rounded-2xl p-6 text-center">
              <div className="text-5xl font-extrabold counter" data-to="180">0</div>
              <div className="text-xs text-gray-300 mt-1">хайров за квартал</div>
            </div>
          </div>
        </section>

        <section id="media" >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Моменты и медиа</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <img className="rounded-xl object-cover w-full h-52 card" src="https://assets.turbologo.com/blog/en/2022/03/04080313/Image_3-1-1536x864.jpg" alt="" />
            <img className="rounded-xl object-cover w-full h-52 card" src="https://ru.paxforex.org/sites/default/files/forex_affiliate.jpg" alt="" />
            <img className="rounded-xl object-cover w-full h-52 card" src="https://i.pinimg.com/originals/f9/95/3c/f9953c0335a6ca05692709410f830e5b.jpg" alt="" />
          </div>
        </section>

        <section id="awards">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Награды</h2>
          <div className="grid gap-6 md:grid-cols-3">
            <article className="card rounded-2xl p-6"><h3 className="text-xl font-semibold">HR Tech Awards, 2024</h3><p className="text-sm text-gray-200 mt-1">«Лучшая интеграция ATS в аутсорсинге найма»</p></article>
            <article className="card rounded-2xl p-6"><h3 className="text-xl font-semibold">Customer Success Stars, 2025</h3><p className="text-sm text-gray-200 mt-1">«Клиентоцентричность и SLA»</p></article>
            <article className="card rounded-2xl p-6"><h3 className="text-xl font-semibold">Recruiting Excellence, 2025</h3><p className="text-sm text-gray-200 mt-1">«Лучший RPO-кейс в ритейле»</p></article>
          </div>
        </section>

        <section id="docs">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Документы и материалы</h2>
          <div className="grid gap-6 md:grid-cols-3">
            <a className="card rounded-2xl p-6 hover:bg-white/10" href="#"><h3 className="text-xl font-semibold">One-pager INTERIM (PDF)</h3><p className="text-sm text-gray-200 mt-1">Кратко о компании и услугах.</p></a>
            <a className="card rounded-2xl p-6 hover:bg-white/10" href="#"><h3 className="text-xl font-semibold">Пресс-кит</h3><p className="text-sm text-gray-200 mt-1">Логотипы, цвета, шаблоны.</p></a>
            <a className="card rounded-2xl p-6 hover:bg-white/10" href="#"><h3 className="text-xl font-semibold">Кейсы (подборка)</h3><p className="text-sm text-gray-200 mt-1">Истории клиентов с цифрами.</p></a>
          </div>
        </section>

        <section className="simple-card p-6 flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-2xl font-bold">Хотите войти в историю INTERIM?</h3>
            <p className="text-sm text-gray-200 mt-1">Расскажите о задаче — вернёмся с планом и сроками.</p>
          </div>
          <a href="/contacts" className="btn btn-accent mt-4 md:mt-0">Связаться</a>
        </section>
      </main>
    </div>
  );
}